import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  UserGroupIcon,
  CubeIcon,
  KeyIcon,
  UserIcon,
  DocumentPlusIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  BellIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  XMarkIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import SupportModal from '../SupportModal';

// isCollapsed is now passed as a prop from DashboardLayout
const Sidebar = ({ isMobileOpen, onMobileClose, isCollapsed }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Admin navigation structure
  const adminNavigation = [
    {
      name: 'Admin Dashboard',
      href: '/dashboard/admin',
      icon: ChartBarIcon,
      description: 'Overview & Analytics'
    },
    {
      name: 'License Requests',
      href: '/dashboard/admin/requests',
      icon: DocumentTextIcon,
      description: 'Review & Approve'
    },
    {
      name: 'Activation Keys',
      href: '/dashboard/admin/activation-keys',
      icon: KeyIcon,
      description: 'Manage Activation Keys'
    },
    {
      name: 'Cluster Licenses',
      href: '/dashboard/admin/cluster-licenses',
      icon: ServerIcon,
      description: 'Configure & Sign Cluster Licenses'
    },
    {
      name: 'X-NAS Licenses',
      href: '/dashboard/admin/xnas-licenses',
      icon: ServerIcon,
      description: 'Issue & Manage X-NAS HA Licenses'
    },
    {
      name: 'nxtVault NAS Licenses',
      href: '/dashboard/admin/nxtvault-licenses',
      icon: ServerIcon,
      description: 'Issue & Manage nxtVault NAS HA Licenses'
    },
    {
      name: 'User Management',
      href: '/dashboard/users',
      icon: UserGroupIcon,
      description: 'Manage Users & Roles'
    },
    {
      name: 'System Settings',
      href: '/dashboard/admin/system-settings',
      icon: Cog6ToothIcon,
      description: 'Configure System Settings'
    }
  ];

  // Customer navigation structure
  const customerNavigation = [
    {
      name: 'Overview',
      href: '/dashboard/overview',
      icon: ChartBarIcon,
      description: 'Dashboard Summary'
    },
    {
      name: 'Licenses',
      href: '/dashboard/my-licenses',
      icon: KeyIcon,
      description: 'Active Licenses'
    },
    {
      name: 'Requests',
      href: '/dashboard/my-requests',
      icon: ClipboardDocumentListIcon,
      description: 'Track Request Status'
    },
    {
      name: 'Activation Keys',
      href: '/dashboard/activation-keys',
      icon: ShieldCheckIcon,
      description: 'Upload Activation Keys'
    },
    {
      name: 'Cluster Licenses',
      href: '/dashboard/cluster-licenses',
      icon: ServerIcon,
      description: 'Upload & Download Cluster Licenses'
    }
  ];

  const navigation = isAdmin ? adminNavigation : customerNavigation;
  const sectionTitle = isAdmin ? 'Administration' : 'License Management';

  return (
    <>
      {/* Support Modal */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar - Fixed overlay that doesn't affect content layout */}
      <div 
        className={`
          fixed
          inset-y-0 left-0
          z-50
          flex flex-col
          bg-white/95 backdrop-blur-lg
          border-r border-gray-200
          shadow-lg
          transition-all duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${isCollapsed ? 'lg:w-16' : 'lg:w-56'}
          w-56
          mt-14
        `}
        style={{
          // Force desktop visibility and width since Tailwind lg: classes don't apply
          width: window.innerWidth >= 1024 ? (isCollapsed ? '64px' : '224px') : '224px',
          transform: window.innerWidth >= 1024 ? 'translateX(0)' : undefined
        }}
      >
        
        <div className="flex grow flex-col gap-y-4 overflow-y-auto px-4 pb-4">

        {/* Navigation */}
        <nav className="flex flex-1 flex-col pt-4">
          <ul role="list" className="flex flex-1 flex-col gap-y-2">
            <li>
              <ul role="list" className="space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      end={item.href === '/dashboard/admin' || item.href === '/dashboard/overview'}
                      onClick={onMobileClose}
                      className={({ isActive }) =>
                        `group flex items-center rounded-lg transition-all duration-200 ${
                          isCollapsed ? 'justify-center p-2' : 'gap-x-2.5 px-3 py-2'
                        } ${
                          isActive
                            ? 'text-white shadow-md shadow-green-500/20'
                            : 'text-gray-700 hover:text-green-700 hover:bg-green-50'
                        }`
                      }
                      style={({ isActive }) => ({
                        backgroundColor: isActive ? '#197560' : undefined
                      })}
                      title={isCollapsed ? item.name : ''}
                    >
                      <item.icon
                        className={`shrink-0 transition-colors duration-200 ${
                          isCollapsed ? 'h-6 w-6' : 'h-5 w-5'
                        }`}
                        aria-hidden="true"
                      />
                      {!isCollapsed && (
                        <span className="flex-1 text-sm font-medium">{item.name}</span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </nav>

        {/* Role Badge */}
        {!isCollapsed && (
          <div className="border-t border-gray-200 pt-3">
            <NavLink
              to="/dashboard/account-settings"
              onClick={onMobileClose}
              className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
            >
              <div className="flex items-center space-x-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  isAdmin ? 'bg-purple-100' : 'bg-blue-100'
                }`}>
                  {isAdmin ? (
                    <ShieldCheckIcon className="w-4 h-4 text-purple-600" />
                  ) : (
                    <UserIcon className="w-4 h-4 text-blue-600" />
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className={`text-xs ${isAdmin ? 'text-purple-600' : 'text-blue-600'}`}>
                    {isAdmin ? 'Administrator' : 'Customer'}
                  </p>
                </div>
              </div>
            </NavLink>
          </div>
        )}
        
        {/* Collapsed Role Icon */}
        {isCollapsed && (
          <div className="border-t border-gray-200 pt-3 flex justify-center">
            <NavLink
              to="/dashboard/account-settings"
              onClick={onMobileClose}
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                isAdmin ? 'bg-purple-100 hover:bg-purple-200' : 'bg-blue-100 hover:bg-blue-200'
              } transition-colors duration-200 cursor-pointer`}
              title={`${user?.firstName} - ${isAdmin ? 'Administrator' : 'Customer'}`}
            >
              {isAdmin ? (
                <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
              ) : (
                <UserIcon className="w-5 h-5 text-blue-600" />
              )}
            </NavLink>
          </div>
        )}

        {/* Footer with Support Link */}
        {!isCollapsed && (
          <div className="space-y-2">
            <button
              onClick={() => setShowSupportModal(true)}
              className="w-full flex items-center gap-x-2 rounded-lg p-2.5 text-xs font-medium text-gray-700 hover:text-green-700 hover:bg-green-50 transition-all duration-200 group"
            >
              <svg className="h-4 w-4 shrink-0 group-hover:text-green-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <div className="text-left">
                <div className="text-xs">Xloud Support</div>
                <div className="text-xs text-gray-500">Get help & assistance</div>
              </div>
            </button>
          </div>
        )}
        
        {/* Collapsed Support Icon */}
        {isCollapsed && (
          <div className="flex justify-center">
            <button
              onClick={() => setShowSupportModal(true)}
              className="p-2 rounded-lg text-gray-700 hover:text-green-700 hover:bg-green-50 transition-all duration-200 group"
              title="Xloud Support"
            >
              <svg className="h-5 w-5 group-hover:text-green-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </button>
          </div>
        )}
      </div>
      </div>

      {/* Support Modal */}
      <SupportModal 
        isOpen={showSupportModal} 
        onClose={() => setShowSupportModal(false)} 
      />
    </>
  );
};

export default Sidebar;